import type { ReactNode } from "react";

import { XIcon } from "@phosphor-icons/react";
import {
  Box,
  Card,
  DropdownMenu,
  Flex,
  IconButton,
  Text,
} from "@radix-ui/themes";

import { WidgetOverflowMenuItems } from "~/v3-poc/components/Widget";
import type { ChartOrderOverlay } from "./tradingView/TradingViewChart.types";

export interface ChartWidgetPresentationProps {
  /**
   * Chart node composed by `ChartWidget.connector`. Other consumers may omit it
   * to render the generic empty state.
   */
  chart?: ReactNode;
  selectedOrderOverlay?: ChartOrderOverlay;
  onOrderDetailsDismiss?: () => void;
  showOpenLimitOrders?: boolean;
  showHistoricalOrderFills?: boolean;
  onOpenLimitOrdersVisibleChange?: (visible: boolean) => void;
  onHistoricalOrderFillsVisibleChange?: (visible: boolean) => void;
}

/**
 * Full-height sizing contract lives on inline styles so it is measurable in
 * tests and works with the TradingView autosize contract regardless of the
 * grid engine wrapping the widget. `minWidth: 0` lets the chart shrink with
 * its grid cell instead of locking to TradingView's intrinsic width.
 */
const FILL_STYLE = {
  height: "100%",
  width: "100%",
  minHeight: "0px",
  minWidth: "0px",
} as const;

export const ChartWidgetPresentation = ({
  chart,
  selectedOrderOverlay,
  onOrderDetailsDismiss,
  showOpenLimitOrders = false,
  showHistoricalOrderFills = false,
  onOpenLimitOrdersVisibleChange,
  onHistoricalOrderFillsVisibleChange,
}: ChartWidgetPresentationProps) => {
  return (
    <>
      <WidgetOverflowMenuItems>
        <DropdownMenu.CheckboxItem
          checked={showOpenLimitOrders}
          onCheckedChange={(checked) =>
            onOpenLimitOrdersVisibleChange?.(checked === true)
          }
        >
          Display open limit orders
        </DropdownMenu.CheckboxItem>
        <DropdownMenu.CheckboxItem
          checked={showHistoricalOrderFills}
          onCheckedChange={(checked) =>
            onHistoricalOrderFillsVisibleChange?.(checked === true)
          }
        >
          Display historical order fills
        </DropdownMenu.CheckboxItem>
      </WidgetOverflowMenuItems>
      {!chart ? (
        <Flex
          align="center"
          justify="center"
          p="4"
          data-chart-widget-shell="empty"
          style={FILL_STYLE}
        >
          <Text size="1" color="gray" align="center">
            No symbol — pick a group
          </Text>
        </Flex>
      ) : (
        <Flex
          direction="column"
          data-chart-widget-shell="mounted"
          style={FILL_STYLE}
        >
          <Box flexGrow="1" minHeight="0" position="relative">
            {chart}
            {selectedOrderOverlay ? (
              <Card
                size="1"
                variant="surface"
                className="absolute top-2 right-2 z-10 max-w-[calc(100%-1rem)]"
              >
                <Flex align="start" gap="2">
                  <Box className="min-w-0 flex-1">
                    <Flex align="center" gap="2" wrap="wrap">
                      <Text size="1" weight="medium" color="gray">
                        {selectedOrderOverlay.side === "buy" ? "Buy" : "Sell"}{" "}
                        {selectedOrderOverlay.kind === "fill"
                          ? `${selectedOrderOverlay.orderType} fill`
                          : "Limit"}
                      </Text>
                      <Text size="1" className="atd-numeric">
                        Qty {selectedOrderOverlay.quantity} at $
                        {selectedOrderOverlay.kind === "fill"
                          ? selectedOrderOverlay.price
                          : selectedOrderOverlay.limitPrice.toFixed(2)}
                      </Text>
                      <Text size="1" color="gray" className="atd-numeric">
                        {selectedOrderOverlay.id}
                      </Text>
                    </Flex>
                  </Box>
                  <IconButton
                    aria-label="Dismiss order details"
                    color="gray"
                    size="1"
                    type="button"
                    variant="ghost"
                    onClick={onOrderDetailsDismiss}
                  >
                    <XIcon aria-hidden size={12} />
                  </IconButton>
                </Flex>
              </Card>
            ) : null}
          </Box>
        </Flex>
      )}
    </>
  );
};
